/**
 * ORE RIG — "Hole Enthusiast" (doc 04). 2x2, 30 ore, 250 HP, -2 power, +2 ore/s.
 *
 * Silhouette family: DERRICK A-FRAME. Four steep legs meeting a small head
 * platform, one cross-brace, a drill string down the middle, and a squat ore
 * silo on the near corner. The head is deliberately small: a narrow top rises
 * tall without occluding the tiles behind it (Law L3).
 *
 * Signature: amber head lamp that blinks while the rig is producing — the one
 * moving part a machine is allowed (doc 10 §Animation).
 */

import { hex } from '../../render/surface.ts';
import type { BuildContext, EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_ECONOMY } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, linePx, post, type EntityCanvas } from '../../render/primitives.ts';

const N = 2;
/** Ore is the one detail colour this entity spends outside the shared ramps. */
const ORE = hex('#93a6ab');

interface Leg {
  fx: number;
  fy: number;
  hx: number;
  hy: number;
}

export const drawOreRig: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_ECONOMY);
  const lamp = signatureColor(sig, ctx.state);
  const working = ctx.state === 'working';

  // --- deck -----------------------------------------------------------------
  // Inset 4 rows so the foundation pad shows all round: the contact outline
  // needs ground to sit on, or two adjacent rigs merge (doc 10 rule 5).
  const deckH = 3;
  isoPrism(c, { n: N, inset: 4, height: deckH, ramp: r });
  const deckTop = c.ay - deckH;
  // Deck lip, so the slab reads as a platform rather than a block.
  isoDiamondRim(c.s, c.ax, deckTop, 12, r.edge);

  // --- derrick --------------------------------------------------------------
  const headY = deckTop - 20;
  const legs: Leg[] = [
    { fx: c.ax, fy: deckTop - 8, hx: c.ax, hy: headY - 3 }, // N, furthest
    { fx: c.ax + 16, fy: deckTop, hx: c.ax + 6, hy: headY }, // E
    { fx: c.ax - 16, fy: deckTop, hx: c.ax - 6, hy: headY }, // W
    { fx: c.ax, fy: deckTop + 8, hx: c.ax, hy: headY + 3 }, // S, nearest
  ];

  linePx(c.s, legs[0]!.fx, legs[0]!.fy, legs[0]!.hx, legs[0]!.hy, r.right, 3);
  linePx(c.s, legs[2]!.fx, legs[2]!.fy, legs[2]!.hx, legs[2]!.hy, r.left, 3);
  linePx(c.s, legs[2]!.fx - 1, legs[2]!.fy, legs[2]!.hx - 1, legs[2]!.hy, r.edge, 1);
  linePx(c.s, legs[1]!.fx, legs[1]!.fy, legs[1]!.hx, legs[1]!.hy, r.right, 3);

  // Cross-brace at half height turns four legs into a truss.
  const mids = legs.map((l) => ({ x: (l.fx + l.hx) >> 1, y: (l.fy + l.hy) >> 1 }));
  const [mN, mE, mW, mS] = [mids[0]!, mids[1]!, mids[2]!, mids[3]!];
  linePx(c.s, mW.x, mW.y, mN.x, mN.y, r.edge, 1);
  linePx(c.s, mN.x, mN.y, mE.x, mE.y, r.edge, 1);
  linePx(c.s, mE.x, mE.y, mS.x, mS.y, r.left, 1);
  linePx(c.s, mS.x, mS.y, mW.x, mW.y, r.left, 1);

  // Drill string with a travelling block riding it while the rig produces.
  post(c, 0, deckTop - headY, 2, r.deep, deckH);
  const travel = working ? Math.round(9 * Math.abs(0.5 - ctx.phase) * 2) : 6;
  c.s.fillRect(c.ax - 3, headY + 4 + travel, 6, 3, r.left);
  c.s.fillRect(c.ax - 3, headY + 4 + travel, 6, 1, r.edge);

  // Head platform: small, so a tall rig still shows the tiles behind it.
  isoPrism(c, { n: 1, inset: 5, height: 3, lift: c.ay - headY, ramp: r });

  // Near leg last, so it reads in front of the truss.
  linePx(c.s, legs[3]!.fx, legs[3]!.fy, legs[3]!.hx, legs[3]!.hy, r.top, 3);
  linePx(c.s, legs[3]!.fx - 1, legs[3]!.fy, legs[3]!.hx - 1, legs[3]!.hy, r.edge, 1);

  // --- ore silo on the near-east corner --------------------------------------
  const siloTop = isoCylinder(c, { rx: 7, height: 14, dx: 13, dy: 6, lift: deckH, ramp: r });
  isoDiamondRim(c.s, c.ax + 13, siloTop, 3, r.edge);
  // banding, so the silo never reads as an extension of the deck
  c.s.hline(c.ax + 7, c.ax + 19, siloTop + 5, r.deep);
  c.s.hline(c.ax + 7, c.ax + 19, siloTop + 10, r.deep);
  // Chute from the derrick into the silo.
  linePx(c.s, c.ax + 3, deckTop - 3, c.ax + 8, deckTop + 0, r.deep, 2);
  if (working || ctx.state === 'idle') {
    c.s.fillRect(c.ax + 10, siloTop - 1, 2, 1, ORE);
    c.s.fillRect(c.ax + 13, siloTop - 2, 3, 1, ORE);
    c.s.fillRect(c.ax + 16, siloTop - 1, 2, 1, ORE);
  }

  // --- signature lamp --------------------------------------------------------
  if (lamp !== null) {
    // Blink window sits strictly inside the cycle so frame 0 == frame N
    // and `animationIntegrity` sees a closed loop.
    const blink = working && ctx.phase > 0.55 && ctx.phase < 0.85;
    const colour = blink ? sig.hot : lamp;
    c.s.fillRect(c.ax - 1, headY - 6, 3, 2, colour);
    if (blink) {
      c.s.px(c.ax, headY - 7, colour);
      c.s.px(c.ax - 2, headY - 5, sig.halo);
      c.s.px(c.ax + 2, headY - 5, sig.halo);
    }
  }
};
